import {
  OutboxEventStatus,
  enumOutboxEventStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Col,
  Row,
  Loading,
  Badge,
  cn,
  humanReadableDateTime,
  DateRangePicker,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronLeft, LucideChevronRight } from 'lucide-react';

import { CoreOutboxEventRecord } from '../../_graphql/queryCoreOutboxEvents';
import {
  $remoteCoreOutboxEvents,
  useSelectedCoreEventId,
  useCorePagination,
  useCoreAggregateTypeFilter,
  useCoreDateRangeFilter,
} from '../../_state/coreEvents.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CoreEntityFilter } from './CoreEntityFilter';

import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: OutboxEventStatus) => {
  switch (status) {
    case enumOutboxEventStatus.Processed:
      return 'success';
    case enumOutboxEventStatus.Pending:
      return 'warning';
    case enumOutboxEventStatus.Failed:
      return 'failure';
    case enumOutboxEventStatus.DeadLetter:
      return 'failure';
    default:
      return 'neutral';
  }
};

type EventTypeTranslations = {
  facility: {
    created: string;
    updated: string;
    deleted: string;
    groupAssigned: string;
    groupRemoved: string;
  };
  facilityGroup: {
    created: string;
    updated: string;
    deleted: string;
  };
  user: {
    created: string;
    updated: string;
    addedToOrganization: string;
    removedFromOrganization: string;
    invited: string;
    adminGranted: string;
    adminRevoked: string;
  };
  organization: {
    created: string;
    updated: string;
    deleted: string;
    provisioned: string;
    entitlementGranted: string;
    entitlementRevoked: string;
    signupCompleted: string;
  };
};

const getEventTypeKey = (
  eventName: string,
  payload: string,
): {
  aggregate: keyof EventTypeTranslations;
  action: string;
} | null => {
  try {
    const parsed = JSON.parse(payload);

    let aggregate: keyof EventTypeTranslations | null = null;
    if (eventName === 'FacilityEvent') aggregate = 'facility';
    else if (eventName === 'FacilityGroupEvent') aggregate = 'facilityGroup';
    else if (eventName === 'UserEvent') aggregate = 'user';
    else if (eventName === 'OrganizationEvent') aggregate = 'organization';

    if (!aggregate) return null;

    let action: string | null = null;
    if (parsed.created) action = 'created';
    else if (parsed.updated) action = 'updated';
    else if (parsed.deleted) action = 'deleted';
    else if (parsed.groupAssigned) action = 'groupAssigned';
    else if (parsed.groupRemoved) action = 'groupRemoved';
    else if (parsed.addedToOrganization) action = 'addedToOrganization';
    else if (parsed.removedFromOrganization) action = 'removedFromOrganization';
    else if (parsed.invited) action = 'invited';
    else if (parsed.adminGranted) action = 'adminGranted';
    else if (parsed.adminRevoked) action = 'adminRevoked';
    else if (parsed.provisioned) action = 'provisioned';
    else if (parsed.entitlementGranted) action = 'entitlementGranted';
    else if (parsed.entitlementRevoked) action = 'entitlementRevoked';
    else if (parsed.signupCompleted) action = 'signupCompleted';

    if (!action) return null;

    return { aggregate, action };
  } catch {
    return null;
  }
};

const getTranslatedEventName = (
  eventName: string,
  payload: string,
  eventTypes: EventTypeTranslations,
): string => {
  const key = getEventTypeKey(eventName, payload);
  if (key) {
    const aggregateTranslations = eventTypes[key.aggregate];
    if (aggregateTranslations && key.action in aggregateTranslations) {
      return (aggregateTranslations as Record<string, string>)[key.action];
    }
  }
  return eventName;
};

type EventCardProps = {
  event: CoreOutboxEventRecord;
  isSelected: boolean;
  onClick: () => void;
  idPrefix: string;
  notAvailable: string;
  eventTypes: EventTypeTranslations;
};

const EventCard = ({
  event,
  isSelected,
  onClick,
  idPrefix,
  notAvailable,
  eventTypes,
}: EventCardProps) => (
  <button
    onClick={onClick}
    className={cn(
      '@EventCard',
      cn(
        'w-full rounded-md border p-3 text-left transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-neutral/10 hover:border-neutral/20 hover:bg-neutral/5',
      ),
    )}
  >
    <Col gap2>
      <Row className="justify-between">
        <span className="text-sm font-medium">
          {getTranslatedEventName(event.eventName, event.payload, eventTypes)}
        </span>
        <Badge
          color={getStatusColor(event.status as OutboxEventStatus)}
          size="small"
        >
          {event.status}
        </Badge>
      </Row>
      <Row className="text-xs text-neutral/60" gap2>
        <span>
          {idPrefix} {event.metadata.aggregateId ?? notAvailable}
        </span>
      </Row>
      <Row className="text-xs text-neutral/40">
        <span>
          {humanReadableDateTime(event.timestamps.createdAt, {
            locale: getLocale(),
            includeYear: true,
            includeSeconds: true,
          })}
        </span>
      </Row>
    </Col>
  </button>
);

export const CoreEventsList = () => {
  const t = useTranslations();
  const [pagination, setPagination] = useCorePagination();
  const [selectedEventId, setSelectedEventId] = useSelectedCoreEventId();
  const [aggregateType] = useCoreAggregateTypeFilter();
  const [dateRange, setDateRange] = useCoreDateRangeFilter();

  const { data, isLoading } = useAtomValue($remoteCoreOutboxEvents);

  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  const hasNextPage = pagination.offset + pagination.limit < total;
  const hasPrevPage = pagination.offset > 0;

  const goToNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const goToPrevPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  if (isLoading && !data) {
    return (
      <Col className="@CoreEventsList flex-1 items-center justify-center border-r border-neutral/10">
        <Loading isLoading />
      </Col>
    );
  }

  return (
    <Col className="@CoreEventsList w-[350px] border-r border-neutral/10">
      <Row className="border-b border-neutral/10 p-3">
        <Col className="flex-1">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            aria-label={t.filters.dateRange.label}
            clearable
            onClear={() => setDateRange(null)}
          />
        </Col>
      </Row>
      {aggregateType && (
        <Col className="border-b border-neutral/10 p-3">
          <CoreEntityFilter />
        </Col>
      )}
      <Col className="flex-1 gap-2 overflow-y-auto p-3">
        {events.length === 0 ? (
          <Col className="flex-1 items-center justify-center text-neutral/40">
            <span>{t.coreEventsView.list.empty}</span>
          </Col>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isSelected={selectedEventId === event.id}
              onClick={() => setSelectedEventId(event.id)}
              idPrefix={t.coreEventsView.list.idPrefix}
              notAvailable={t.coreEventsView.list.notAvailable}
              eventTypes={t.coreEventsView.detail.eventTypes}
            />
          ))
        )}
      </Col>

      <Row className="items-center justify-between border-t border-neutral/10 px-3 py-2">
        <span className="text-xs text-neutral/60">
          {pagination.offset + 1} -{' '}
          {Math.min(pagination.offset + pagination.limit, total)}{' '}
          {t.pagination.of} {total}
        </span>
        <Row gap1>
          <button
            onClick={goToPrevPage}
            disabled={!hasPrevPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasPrevPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronLeft size={16} />
          </button>
          <button
            onClick={goToNextPage}
            disabled={!hasNextPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasNextPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronRight size={16} />
          </button>
        </Row>
      </Row>
    </Col>
  );
};
